import { Host, h } from "@stencil/core";
import { adoptCommonThemes } from "../../common/theme";
import { SCROLLABLE_CLASS } from "../../common/reserved-names";
import { parseCodeToJSX } from "./internal/code-highlight";
/**
 * A control to highlight code blocks.
 * - It supports code highlight by parsing the incoming code string to [hast](https://github.com/micromark/micromark-extension-gfm) using [lowlight](lowlight). After that, it implements a reactivity layer by implementing its own render for the hast.
 *
 * - It also supports all programming languages from [highlight.js](https://github.com/highlightjs/highlight.js).
 *
 * - When the code highlighting is needed at runtime, the control will load on demand the code parser and the programming language needed to parse the code.
 */
export class ChCode {
    constructor() {
        this.JSXCodeBlock = undefined;
        this.language = undefined;
        this.lastNestedChildClass = "last-nested-child";
        this.showIndicator = false;
        this.value = undefined;
    }
    #lastNestedChildIsRoot = true;
    el;
    #getLanguageOrDefault = () => this.language || "plaintext";
    #parseCodeToJSX = () => parseCodeToJSX(this.value, this.#getLanguageOrDefault(), this.showIndicator, this.lastNestedChildClass);
    connectedCallback() {
        adoptCommonThemes(this.el.shadowRoot.adoptedStyleSheets);
    }
    async componentWillRender() {
        if (this.value) {
            const renderResult = await this.#parseCodeToJSX();
            this.JSXCodeBlock = renderResult.renderedCode;
            this.#lastNestedChildIsRoot = renderResult.lastNestedChildIsRoot;
        }
        else {
            this.JSXCodeBlock = "";
            this.#lastNestedChildIsRoot = true;
        }
    }
    render() {
        const addLastNestedChildClassInHost = this.showIndicator && this.#lastNestedChildIsRoot;
        const language = this.#getLanguageOrDefault();
        return (h(Host, { key: '0c70e8ce6107279dc632da6751be885e9df10f0a', class: {
                "ch-code-show-indicator": this.showIndicator,
                [SCROLLABLE_CLASS]: true
            } }, h("code", { key: '1df73baeb702656996758306cace4f34cd5e8ef3', class: {
                [`hljs language-${language}`]: true,
                [this.lastNestedChildClass]: addLastNestedChildClassInHost
            }, part: `code language-${language}` }, this.JSXCodeBlock)));
    }
    static get is() { return "ch-code"; }
    static get encapsulation() { return "shadow"; }
    static get originalStyleUrls() {
        return {
            "$": ["code.scss"]
        };
    }
    static get styleUrls() {
        return {
            "$": ["code.css"]
        };
    }
    static get properties() {
        return {
            "language": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string | undefined",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "Specifies the code language to highlight."
                },
                "attribute": "language",
                "reflect": false
            },
            "lastNestedChildClass": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "attribute": "last-nested-child-class",
                "reflect": false,
                "defaultValue": "\"last-nested-child\""
            },
            "showIndicator": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Specifies if an indicator is displayed in the last element rendered.\nUseful for streaming scenarios where a loading indicator is needed."
                },
                "attribute": "show-indicator",
                "reflect": false,
                "defaultValue": "false"
            },
            "value": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string | undefined",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "Specifies the code string to highlight."
                },
                "attribute": "value",
                "reflect": false
            }
        };
    }
    static get states() {
        return {
            "JSXCodeBlock": {}
        };
    }
    static get elementRef() { return "el"; }
}
